import { useEffect } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { cn } from '@lyearn/style';
import { BaseButton, Tooltip, Typography } from '@lyearn/ui';
import Loader from '@/components/Loader';

import LabelCategoryMenu from '../../features/actionItems/components/ActionStatus/LabelCategoryMenu';
import {
  LabelCategoryFragment,
  useLabelCategoryQuery,
} from '../../graphql/queries/generated/labelCategory';
import { baseButtonFocusStyles, TASK_PILL_VARIANT_CONFIG, TaskPillVariant } from './styles';

interface Props {
  labelCategoryId?: string | null;
  className?: string;
  editable?: boolean;
  onChange: (labelCategory: any, id?: string) => void;
  variant: TaskPillVariant;
  labelCategory?: LabelCategoryFragment | null;
  mode?: 'create' | 'update';
  disableInteractiveTooltip?: boolean;
}

const TaskLabel = (props: Props) => {
  const {
    className,
    variant,
    editable,
    labelCategoryId,
    labelCategory: labelCategoryProp,
    onChange,
    mode,
    disableInteractiveTooltip = false,
  } = props;

  const [{ data, fetching, error }] = useLabelCategoryQuery({
    pause: !!labelCategoryProp || !labelCategoryId,
    variables: {
      filters: {
        labelCategoryId: labelCategoryId!,
      },
    },
  });
  const labelCategoryData = data?.labelCategory.labelCategory;
  const [isOpen, { toggle }] = useToggle(false);

  const labelCategory = labelCategoryProp ?? labelCategoryData;

  const variantConfig = TASK_PILL_VARIANT_CONFIG[variant];

  useEffect(() => {
    if (!labelCategoryId && labelCategory && mode !== 'update') {
      onChange(labelCategory, undefined);
    }
  }, [onChange, labelCategory, labelCategoryId, mode]);

  const renderTaskLabel = () => (
    <BaseButton
      aria-label="label"
      className={cn(
        'w-max p-0 space-s-8',
        baseButtonFocusStyles,
        variantConfig.root.className,
        className,
        { [variantConfig.rootEditable.className]: editable },
      )}
      disabled={!editable}>
      {fetching ? (
        <Loader className="flex-grow-0" showLabel={false} size={18} />
      ) : (
        <>
          <Tooltip disableInteractive={disableInteractiveTooltip} title={labelCategory?.name}>
            <Typography
              className={variantConfig.label.className}
              color={variantConfig.label.color}
              variant="body-short-02">
              {labelCategory?.label}
            </Typography>
          </Tooltip>
        </>
      )}
    </BaseButton>
  );

  if (editable) {
    return (
      <LabelCategoryMenu
        labelCategory={labelCategory}
        open={isOpen}
        popoverPosition={variantConfig.popoverPosition}
        onLabelCategoryChange={onChange}
        onOpenChange={toggle}>
        {renderTaskLabel()}
      </LabelCategoryMenu>
    );
  }

  return renderTaskLabel();
};

export default TaskLabel;
